// Implements: F-MCK-001, F-CNV-003
/**
 * What creating a mockup hands to the page it lands on (owner decision OD-7, 7.6).
 *
 * The creation already started the first turn on the server, in the same call as the mockup
 * (`POST /mockups/conversation`). The page it lands on must not start anything: it is handed the
 * mockup, the turn and the run the server named, and only FOLLOWS that turn (the state's `turn`,
 * then `last_turn` matched by the turn id). Nothing paid is sent from there.
 *
 * It travels in the router's navigation state, which the browser keeps in the history entry, so
 * it is read defensively: anything can be in a history entry, including a hand-off of an older
 * shape, which is ignored.
 */

export interface FirstTurnHandoff {
  /** The mockup the hand-off was made for: a page for another mockup ignores it. */
  readonly mockupId: string;
  readonly turnId: string;
  readonly generationId: string;
}

const KEY = "firstTurn";

export function handoffState(handoff: FirstTurnHandoff): Record<string, FirstTurnHandoff> {
  return { [KEY]: handoff };
}

function nonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

export function readHandoff(state: unknown, mockupId: string): FirstTurnHandoff | null {
  if (typeof state !== "object" || state === null) return null;
  const raw = (state as Record<string, unknown>)[KEY];
  if (typeof raw !== "object" || raw === null) return null;
  const candidate = raw as Record<string, unknown>;
  if (
    nonEmpty(candidate.mockupId) &&
    candidate.mockupId === mockupId &&
    nonEmpty(candidate.turnId) &&
    nonEmpty(candidate.generationId)
  ) {
    return { mockupId: candidate.mockupId, turnId: candidate.turnId, generationId: candidate.generationId };
  }
  return null;
}

/** The history state without the hand-off, and without anything else's keys touched. */
export function clearHandoff(state: unknown): Record<string, unknown> | null {
  if (typeof state !== "object" || state === null) return null;
  const rest = Object.fromEntries(
    Object.entries(state as Record<string, unknown>).filter(([key]) => key !== KEY),
  );
  return Object.keys(rest).length === 0 ? null : rest;
}
